import {Component, OnDestroy, OnInit} from '@angular/core';
import {Update} from '@ngrx/entity';
import {Observable, Subscription} from 'rxjs';
import {ActivatedRoute, Router} from '@angular/router';
import {LayoutConfigService, SubheaderService} from '../../../../core/_base/layout';
import {AppState} from '../../../../core/reducers';
import {LayoutUtilsService} from '../../../../core/_base/crud';
import {UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';
import {select, Store} from '@ngrx/store';
import {delay} from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import {
    BuildingType,
    BuildingTypeOnServerCreated,
    BuildingTypeUpdated,
    selectBuildingTypeById,
    selectBuildingTypesActionLoading,
    selectLastCreatedBuildingTypeId
} from '../../../../core/linked-tables';
import {combineLatest} from 'rxjs'

@Component({
    selector: 'kt-building-type-edit',
    templateUrl: './building-type-edit.component.html',
    styleUrls: ['./building-type-edit.component.scss']
})


export class BuildingTypeEditComponent implements OnInit, OnDestroy {

    buildingType: BuildingType;
    oldBuildingType: BuildingType;

    buildingTypeForm: UntypedFormGroup;
    hasFormErrors = false;

    loading$: Observable<boolean>;
    viewLoading: boolean = false;
    loadingAfterSubmit: boolean = false;
    mode = 'NEW';
    readonly = false
    // Private properties
    private componentSubscriptions: Subscription;
    private routeSubscription: Subscription;

    /**
     * Component constructor
     *
     * @param activatedRoute
     * @param router
     * @param fb: FormBuilder
     * @param layoutUtilsService
     * @param subheaderService
     * @param store: Store<AppState>
     * @param layoutConfigService
     */
    constructor(private activatedRoute: ActivatedRoute,
                private router: Router,
                private fb: UntypedFormBuilder,
                private layoutUtilsService: LayoutUtilsService,
                public subheaderService: SubheaderService,
                private store: Store<AppState>,
                private layoutConfigService: LayoutConfigService,
                private translate: TranslateService) {
    }

    ngOnInit() {
        this.loading$ = this.store.pipe(select(selectBuildingTypesActionLoading));
        this.routeSubscription = combineLatest([
            this.activatedRoute.params,
            this.activatedRoute.data
        ]).subscribe(([params, data]) => {
            const id = params.id;
            this.readonly = data.readonly
            if (id && id > 0) {
                this.store.pipe(select(selectBuildingTypeById(id))).subscribe(res => {
                    if (res) {
                        this.oldBuildingType = Object.assign({}, res);
                        this.buildingType = Object.assign({}, this.oldBuildingType);
                        this.initBuildingType();
                    }
                });
            } else {
                this.buildingType = new BuildingType();
                this.buildingType.clear();
                this.initBuildingType();
            }
        });

        if (this.activatedRoute.snapshot.paramMap.get('id') != null) {
            this.setBreadCrumbs(Number(this.activatedRoute.snapshot.paramMap.get('id')));
        } else {
            this.setBreadCrumbs(0);
        }
    }

    setBreadCrumbs(id: number): void {
        this.subheaderService.setTitle(this.getComponentTitle());
        this.subheaderService.setBreadcrumbs([
            {title: 'Developer', page: `../default/admin-management`},
            {title: 'Linked tables', page: `../default/admin-management/linked-tables`},
            {title: 'Building Types', page: `../default/admin-management/linked-tables/base-of-values`},
            {
                title: this.getComponentTitle(),
                page: id > 0 ? `../default/admin-management/linked-tables/base-of-values/edit/${id}`
                    : `../default/admin-management/linked-tables/base-of-values/add`
            }
        ]);
    }

    ngOnDestroy(): void {
        if (this.componentSubscriptions) {
            this.componentSubscriptions.unsubscribe();
        }
        if (this.routeSubscription) {
            this.routeSubscription.unsubscribe();
        }
    }

    initBuildingType() {
        this.createForm();
        // set subheaderService
    }


    createForm() {
        this.buildingTypeForm = this.fb.group({
            name: [this.buildingType.name, Validators.required],
        });
    }

    /**
     * Returns page title
     */
    getComponentTitle(): string {
        if (this.buildingType && this.buildingType.id > 0) {
            this.mode = 'EDIT';
            return this.translate.instant('BUILDING_TYPE.FORM.TITLE.EDIT', {name: this.buildingType.name});
        }

        return this.translate.instant('BUILDING_TYPE.FORM.TITLE.NEW');
    }

    /**
     * Check control is invalid
     * @param controlName: string
     */
    isControlInvalid(controlName: string): boolean {
        const control = this.buildingTypeForm.controls[controlName];
        return control.invalid && control.touched;
    }

    reset() {
        this.buildingType = Object.assign({}, this.oldBuildingType);
        this.createForm();
        this.hasFormErrors = false;
        this.buildingTypeForm.markAsPristine();
        this.buildingTypeForm.markAsUntouched();
        this.buildingTypeForm.updateValueAndValidity();
    }

    /** ACTIONS */

    /**
     * On Submit
     */
    onSubmit(withBack: boolean = false) {
        this.hasFormErrors = false;
        const controls = this.buildingTypeForm.controls;
        /** check form */
        if (this.buildingTypeForm.invalid) {
            Object.keys(controls).forEach(controlName =>
                controls[controlName].markAsTouched()
            );

            this.hasFormErrors = true;
            return;
        }

        if (this.buildingType.id > 0) {
            this.updateBuildingType(this.buildingType);
        } else {
            this.createBuildingType(this.buildingType);
        }
    }

    /**
     * Update buildingType
     *
     * @param _buildingType: BuildingTypeModel
     */
    updateBuildingType(_buildingType: BuildingType) {
        this.loadingAfterSubmit = true;
        this.viewLoading = true;

        const updateBuildingType: Update<BuildingType> = {
            id: _buildingType.id,
            changes: _buildingType
        };
        this.store.dispatch(new BuildingTypeUpdated({
            partialBuildingType: updateBuildingType,
            buildingType: _buildingType
        }));

        const message = `Building Type successfully has been saved.`;
        this.navigateToParent();

    }

    /**
     * Create buildingType
     *
     * @param _buildingType: BuildingTypeModel
     */
    createBuildingType(_buildingType: BuildingType) {
        this.store.dispatch(new BuildingTypeOnServerCreated({buildingType: _buildingType}));
        this.componentSubscriptions = this.store.pipe(
            select(selectLastCreatedBuildingTypeId),
            delay(1000), // Remove this line
        ).subscribe(res => {
            if (!res) {
                return;
            }
            this.navigateToParent();
        });
    }

    /** Alect Close event */
    onAlertClose($event) {
        this.hasFormErrors = false;
    }


    isFormValid() {
        return (this.buildingType && this.buildingType.name && this.buildingType.name.length > 0);
    }

    navigateToParent() {
        let url = '../';
        if (this.buildingType.id > 0) {
            url = '../../';
        }
        this.router.navigate([url], {relativeTo: this.activatedRoute});
    }

}
